import type { ComparisonItem, ComparisonRowDef } from "@highereducation/hen-ui";
import type { DataPoints } from "@/monarch/types/ProductCompare";
import type { PricingInfo, ProductCompareData } from "./useProductCompareData";
import type { ProductCompareMessages } from "@/i18n/hooks/useProductCompareMessages";
import { DEFAULT_UNAVAILABLE } from "./constants";

/**
 * Builds the row definitions for the comparison table based on the
 * default row definitions and optional Monarch `dataPoints` configuration.
 *
 * - If `dataPoints` is provided and non-empty, only the configured rows are
 *   returned in the order Monarch specifies.
 * - If `dataPoints` is empty or not provided, `defaultRowDefs` is returned.
 */
export function buildRowDefs(
  rowDefs: ComparisonRowDef[],
  dataPoints?: { dataPoint: DataPoints }[] | null,
): ComparisonRowDef[] {
  if (dataPoints && dataPoints.length > 0) {
    return dataPoints
      .map(({ dataPoint }) => rowDefs.find((r) => r.key === dataPoint))
      .filter((r): r is ComparisonRowDef => r !== undefined);
  }
  return rowDefs;
}

/**
 * Filters out rows from `rowDefs` where every product's field value for
 * that row is UNAVAILABLE (or, for badgeList rows, has no badges).
 * This prevents rendering rows that carry no useful data for any product.
 */
export function filterEmptyRows(
  rowDefs: ComparisonRowDef[],
  items: ComparisonItem[],
  unavailable: string = DEFAULT_UNAVAILABLE,
): ComparisonRowDef[] {
  if (items.length === 0) return [];
  return rowDefs.filter((row) => {
    return items.some((item) => {
      const field = item.fields?.[row.key];
      if (!field) return false;
      if ("primary" in field) return field.primary !== unavailable;
      if ("text" in field) return field.text !== unavailable;
      if ("badges" in field) return (field.badges ?? []).length > 0;
      return true;
    });
  });
}

export function normalizeProductUuids(
  productUuids?: string[] | null,
  maxItems = 4,
): string[] | null | undefined {
  if (!productUuids || productUuids.length === 0) {
    return productUuids;
  }
  const currentProductUuid = productUuids[0];
  const comparisonUuids = Array.from(
    new Set(
      productUuids
        .slice(1)
        .filter((uuid) => uuid && uuid !== currentProductUuid),
    ),
  ).slice(0, maxItems - 1);
  return [currentProductUuid, ...comparisonUuids].filter(
    (uuid): uuid is string => Boolean(uuid),
  );
}

/**
 * Formats a single price amount using Intl.NumberFormat so the correct
 * currency symbol (and code for ambiguous symbols) is always shown.
 * Mirrors the `formatProgramPrice` util used elsewhere in the codebase.
 */
export function formatComparePrice(
  amount: number,
  currency: string = "USD",
): string {
  const hasDecimal = amount % 1 !== 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: hasDecimal ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function getPrimaryPrice(
  pricingInfo: PricingInfo | undefined,
  unavailable: string = DEFAULT_UNAVAILABLE,
): string {
  const currency = pricingInfo?.currency ?? "USD";

  if (pricingInfo?.discountedPrice != null) {
    return formatComparePrice(pricingInfo.discountedPrice, currency);
  }

  if (pricingInfo?.price != null) {
    return formatComparePrice(pricingInfo.price, currency);
  }

  if (pricingInfo?.min != null) {
    if (pricingInfo.max != null) {
      return `${formatComparePrice(pricingInfo.min, currency)} - ${formatComparePrice(pricingInfo.max, currency)}`;
    }

    return formatComparePrice(pricingInfo.min, currency);
  }

  return unavailable;
}

export function getPriceStrike(
  pricingInfo: PricingInfo | undefined,
  showPriceStrike: boolean,
): string | undefined {
  if (!showPriceStrike) {
    return undefined;
  }

  if (
    pricingInfo?.discountedPrice == null ||
    pricingInfo.originalPrice == null
  ) {
    return undefined;
  }

  if (pricingInfo.discountedPrice >= pricingInfo.originalPrice) {
    return undefined;
  }

  return formatComparePrice(
    pricingInfo.originalPrice,
    pricingInfo.currency ?? "USD",
  );
}

/**
 * Returns the rounded discount percentage (e.g. 20) when a real discount exists,
 * or null when either price is missing or there is no actual reduction.
 */
export function calculateDiscountPercentage(
  pricingInfo?: PricingInfo,
): number | null {
  if (
    pricingInfo?.discountedPrice == null ||
    pricingInfo.originalPrice == null ||
    pricingInfo.originalPrice === 0 ||
    pricingInfo.discountedPrice >= pricingInfo.originalPrice
  ) {
    return null;
  }
  return Math.round(
    ((pricingInfo.originalPrice - pricingInfo.discountedPrice) /
      pricingInfo.originalPrice) *
      100,
  );
}

const WEEKS_THRESHOLD = 6;

/**
 * Formats a week count as a duration string.
 * Shows weeks for 6 weeks or fewer; converts to months (ceil(weeks / 4)) above that.
 */
export function formatDuration(weeks: number): string {
  if (weeks <= WEEKS_THRESHOLD) {
    return `${weeks} week${weeks !== 1 ? "s" : ""}`;
  }
  const months = Math.ceil(weeks / 4);
  return `${months} month${months !== 1 ? "s" : ""}`;
}

export interface MapProductsOptions {
  currentProductUuid?: string;
  currentCourseCheckoutUrl?: string | null;
  ctaLabel?: string | null;
  currentCtaLabel?: string | null;
  schoolNameAsTitle?: boolean;
  topCta?: boolean;
  bottomCta?: boolean;
  showBundleBadge?: boolean;
  showPriceStrike?: boolean;
  messages: ProductCompareMessages;
}

export function mapProductsToComparisonItems(
  products: ProductCompareData[],
  options: MapProductsOptions,
): ComparisonItem[] {
  const {
    currentProductUuid,
    currentCourseCheckoutUrl,
    ctaLabel,
    currentCtaLabel,
    schoolNameAsTitle,
    topCta = true,
    bottomCta = true,
    showBundleBadge = true,
    showPriceStrike = true,
    messages,
  } = options;

  return products.map((product) => {
    const isCurrentCourse = product.productUuid === currentProductUuid;
    const isProgram =
      isCurrentCourse &&
      product.productType != null &&
      !product.productType.toLowerCase().includes("course");
    const currentBadgeText = isProgram
      ? messages.currentProgram
      : messages.currentCourse;

    // For the current product:
    // - When currentCourseCheckoutUrl is provided, the caller has confirmed enrollment
    //   is active and supplied the correct routing URL (CYT / track-select for courses,
    //   commerce-coordinator for programs). The CTA is shown.
    // - When currentCourseCheckoutUrl is null/undefined, the caller has signalled that
    //   enrollment is disabled on the PDP (archived course, non-enrollable program, etc.).
    //   The CTA is hidden entirely — we never fall back to the product slug, as
    //   redirecting back to the same page the user is already on is pointless.
    // Secondary columns link to their own PDP when productSlug is available;
    // the CTA is omitted when productSlug is missing.
    const ctaHref = isCurrentCourse
      ? (currentCourseCheckoutUrl ?? null)
      : product.productSlug
        ? `/${product.productSlug}`
        : null;

    const ctaLabelResolved = isCurrentCourse
      ? (currentCtaLabel ?? messages.earnCertificate)
      : (ctaLabel ?? messages.learnMore);

    // Whether to render a CTA for this column:
    // - Current product: only when an enrollment URL was provided (enrollment is active).
    // - Secondary products: only when productSlug is available to form a PDP link.
    const showCta = ctaHref != null;

    return {
      id: product.productUuid,
      ...(isCurrentCourse
        ? {
            badge: {
              text: currentBadgeText,
              variant: "secondary" as const,
              size: "sm" as const,
            },
          }
        : {}),
      header: {
        image: {
          src: product.partnerLogoUrl ?? "",
          alt: product.partnerName ?? messages.partnerLogoAlt,
        },
        title: schoolNameAsTitle
          ? (product.partnerName ?? product.productName)
          : product.productName,
        subtext: schoolNameAsTitle ? product.productName : product.partnerName,
        ...(topCta && showCta
          ? {
              ctaTop: {
                label: ctaLabelResolved,
                href: ctaHref,
              },
            }
          : {}),
      },
      ...(bottomCta && showCta
        ? {
            ctaBottom: {
              label: ctaLabelResolved,
              href: ctaHref,
            },
          }
        : {}),
      // fields keys must match rowDefs keys exactly
      fields: {
        description: {
          text: product.shortDescription ?? messages.unavailable,
        },
        skills: {
          badges: (product.skills ?? []).slice(0, 5).map((skill) => ({
            text: skill,
            variant: "neutral" as const,
          })),
        },
        learningOutcome: {
          primary: messages.certificates(Math.max(1, product.courseCount ?? 1)),
          ...(product.isNew
            ? {
                badge: {
                  text: messages.newBadge,
                  variant: "dark" as const,
                  size: "sm" as const,
                },
              }
            : {}),
        },
        price: {
          primary: getPrimaryPrice(product.pricingInfo, messages.unavailable),
          strike: getPriceStrike(product.pricingInfo, showPriceStrike),
          ...(showBundleBadge &&
          product.pricingInfo?.discountedPrice != null &&
          product.pricingInfo?.originalPrice != null &&
          product.pricingInfo.discountedPrice <
            product.pricingInfo.originalPrice
            ? {
                badge: {
                  text: messages.discountOff(
                    calculateDiscountPercentage(product.pricingInfo) ?? 0,
                  ),
                  variant: "accent" as const,
                  size: "sm" as const,
                  icon: "tag" as const,
                },
              }
            : {}),
        },
        duration: {
          primary:
            product.weeksToComplete != null
              ? messages.formatDuration(product.weeksToComplete)
              : messages.unavailable,
        },
        skillLevel: { primary: product.level ?? messages.unavailable },
        flexibility: { primary: product.flexibility ?? messages.unavailable },
        subject: { primary: product.attributes?.[0] ?? messages.unavailable },
        courseLanguage: {
          primary: product.language ?? messages.unavailable,
        },
      },
    };
  });
}
